import { useEffect, useRef } from "react"
import type { MouseEvent, ReactNode } from "react"

interface WideDialogProps {
  titleId: string
  title: ReactNode
  description?: ReactNode
  onClose: () => void
  children: ReactNode
}

/**
 * 넓은 모달 다이얼로그.
 *
 * 대진표 트리처럼 폭이 큰 내용을 화면 하단에 펼치지 않고 위에 띄운다.
 * 머리(제목 · 닫기)는 고정하고 몸통만 스크롤한다.
 * 닫기 버튼 · Esc · 바깥 영역 클릭으로 닫고, 열려 있는 동안 뒤 화면은 스크롤하지 않는다.
 */
export default function WideDialog({
  titleId,
  title,
  description,
  onClose,
  children,
}: Readonly<WideDialogProps>) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  // 부모가 렌더마다 새 함수를 넘겨도 열 때 한 번만 걸도록 최신 콜백을 ref 로 본다
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    function onKeyDownDocument(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", onKeyDownDocument)
    closeButtonRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", onKeyDownDocument)
    }
  }, [])

  /** 바깥(어두운 영역)을 눌렀을 때만 닫는다 — 다이얼로그 안 클릭은 무시한다 */
  function onClickBackdrop(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <div
      className="app-dialog-backdrop"
      onClick={onClickBackdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="app-dialog app-dialog-wide"
      >
        <div className="app-dialog-wide-head">
          <div className="min-w-0">
            <h3
              id={titleId}
              className="app-txt-h2"
            >
              {title}
            </h3>
            {description && (
              <p className="app-txt-body2 mt-1 text-app-text-200">
                {description}
              </p>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="focus-ring app-btn app-btn-outline app-btn-sm shrink-0"
          >
            닫기
          </button>
        </div>

        <div className="app-dialog-wide-body">{children}</div>
      </div>
    </div>
  )
}
